<script setup lang="ts">
/**
 * 源站 WufuScratchModal：标题图 + 装饰框 + 手刮 + 收集槽 + 规则/任务视图。
 */
import { computed, nextTick, onBeforeUnmount, ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import {
  claimActivityReward,
  getActivityDetail,
  type ActivityDetail,
} from '@/features/activity/activity-api';
import OroMxScratchCard from '@/features/scratch/OroMxScratchCard.vue';
import { isAuthError } from '@/shared/api/auth-failure';
import {
  claimScratchSet,
  confirmScratch,
  getScratchAlbum,
  newScratchRequestNo,
  openScratch,
  type ScratchCard,
} from '@/features/scratch/scratch-api';
import { t, tf } from '@/shared/i18n/messages';
import { oroMxAssetPaths } from '@/shared/theme/oro-mx-home-data';
import {
  oroMxDailyTasks,
  oroMxScratchAssets,
  oroMxScratchCollectKeys,
} from '@/shared/theme/oro-mx-page-data';
import { useAppToastStore } from '@/stores/app-toast-store';
import { useAuthModalStore } from '@/stores/auth-modal-store';
import { useOroMxShellStore } from '@/stores/oro-mx-shell-store';
import { useSessionStore } from '@/stores/session-store';

type ScratchView = 'main' | 'rules' | 'tasks';
type ScratchTaskAction = 'goComplete' | 'goBet' | 'claim' | 'claimed';

type ScratchTaskRow = {
  action: ScratchTaskAction;
  chances: number;
  id: string;
  name: string;
};

const COLLECT_TARGET = 5;
const MX_DAILY_ID = 'mx-daily-scratch';
const DEFAULT_CHANCES: Record<string, number> = {
  invite: 2,
  bet100: 1,
  bet1000: 2,
  bet5000: 5,
};

const shell = useOroMxShellStore();
const sessionStore = useSessionStore();
const authModal = useAuthModalStore();
const appToast = useAppToastStore();
const router = useRouter();

const tickets = ref(0);
const cards = ref<ScratchCard[]>([]);
const canClaimSet = ref(false);
const needFirstDeposit = ref(false);
const jackpotMinor = ref(5000);
const unavailable = ref(false);
const loadingAlbum = ref(false);
const opening = ref(false);
const confirming = ref(false);
const claiming = ref(false);
const sessionReady = ref(false);
const requestNo = ref('');
const prizeKey = ref('');
const prizeTitle = ref('');
const revealOpen = ref(false);
const highlightKey = ref('');
const view = ref<ScratchView>('main');
const dailyDetail = ref<ActivityDetail | null>(null);
const loadingTasks = ref(false);
const claimingTaskKey = ref('');
let highlightTimer = 0;
const scratchRef = ref<{ reset: () => Promise<void> } | null>(null);

const sprite = oroMxAssetPaths.sprite;
const collectSlots = computed(() =>
  oroMxScratchCollectKeys.map((cardKey) => ({
    cardKey,
    qty: cards.value.find((card) => card.cardKey === cardKey)?.qty ?? 0,
  })),
);

const scratchDisabled = computed(
  () => !sessionReady.value || opening.value || confirming.value || claiming.value || unavailable.value,
);

const canRedeem = computed(() => canClaimSet.value || needFirstDeposit.value);

const taskRows = computed<ScratchTaskRow[]>(() =>
  oroMxDailyTasks.map((stage) => {
    const tier = dailyDetail.value?.tiers.find((item) => item.grantKey === stage.id);
    const chances = tier?.rewards?.find((item) => item.itemType === 'SCRATCH_CHANCE')?.qty
      ?? DEFAULT_CHANCES[stage.id]
      ?? 1;
    let action: ScratchTaskAction = stage.id === 'invite' ? 'goComplete' : 'goBet';
    if (tier?.status === 'CLAIMED') {
      action = 'claimed';
    } else if (tier?.status === 'CLAIMABLE') {
      action = 'claim';
    }
    return {
      action,
      chances,
      id: stage.id,
      name: tf(`oro.sign.${stage.id}.name`, stage.name),
    };
  }),
);

function cardSrc(cardKey: string) {
  return oroMxScratchAssets.cards[cardKey] ?? '';
}

function showToast(message: string) {
  appToast.error(message);
}

function flashCard(cardKey: string) {
  highlightKey.value = cardKey;
  window.clearTimeout(highlightTimer);
  highlightTimer = window.setTimeout(() => {
    highlightKey.value = '';
  }, 1400);
}

function resetSessionVisual() {
  sessionReady.value = false;
  requestNo.value = '';
  prizeKey.value = '';
  prizeTitle.value = '';
}

function applyAlbum(album: Awaited<ReturnType<typeof getScratchAlbum>>) {
  tickets.value = album.tickets;
  cards.value = album.cards;
  canClaimSet.value = album.canClaimSet;
  needFirstDeposit.value = album.needFirstDeposit === true;
  jackpotMinor.value = album.jackpotMinor;
  unavailable.value = false;
}

async function loadAlbum() {
  if (!sessionStore.token) {
    unavailable.value = true;
    tickets.value = 0;
    cards.value = [];
    canClaimSet.value = false;
    needFirstDeposit.value = false;
    return null;
  }
  loadingAlbum.value = true;
  try {
    const album = await getScratchAlbum(sessionStore.token);
    applyAlbum(album);
    return album;
  } catch (error) {
    if (isAuthError(error)) {
      tickets.value = 0;
      cards.value = [];
      canClaimSet.value = false;
      needFirstDeposit.value = false;
      return null;
    }
    unavailable.value = true;
    showToast(t('oro.scratch.unavailable'));
    return null;
  } finally {
    loadingAlbum.value = false;
  }
}

async function armScratch(cardKey: string, title: string, clientRequestNo: string) {
  requestNo.value = clientRequestNo;
  prizeKey.value = cardKey;
  prizeTitle.value = title;
  await nextTick();
  await scratchRef.value?.reset();
  sessionReady.value = true;
}

async function startSession() {
  if (opening.value || confirming.value || sessionReady.value || revealOpen.value) {
    return;
  }
  if (unavailable.value) {
    showToast(t('oro.scratch.unavailable'));
    return;
  }
  if (tickets.value <= 0) {
    showToast(t('oro.scratch.noTickets'));
    return;
  }
  if (!sessionStore.token) {
    return;
  }
  opening.value = true;
  sessionReady.value = false;
  try {
    const result = await openScratch(sessionStore.token, newScratchRequestNo('s'));
    if (!shell.scratchOpen) {
      return;
    }
    await armScratch(result.cardKey, result.title, result.clientRequestNo);
  } catch (error) {
    resetSessionVisual();
    if (isAuthError(error)) {
      return;
    }
    showToast(tickets.value <= 0 ? t('oro.scratch.noTickets') : t('oro.scratch.unavailable'));
  } finally {
    opening.value = false;
  }
}

async function onScratchComplete() {
  if (confirming.value || !requestNo.value || !prizeKey.value || !sessionStore.token) {
    return;
  }
  confirming.value = true;
  try {
    const result = await confirmScratch(sessionStore.token, requestNo.value, prizeKey.value);
    tickets.value = result.ticketsAfter;
    cards.value = cards.value.map((card) =>
      card.cardKey === result.cardKey ? { ...card, qty: result.qtyAfter } : card,
    );
    await loadAlbum();
    revealOpen.value = true;
  } catch (error) {
    if (isAuthError(error)) {
      return;
    }
    showToast(t('oro.scratch.unavailable'));
  } finally {
    confirming.value = false;
    sessionReady.value = false;
  }
}

async function onRevealConfirm() {
  const won = prizeKey.value;
  revealOpen.value = false;
  resetSessionVisual();
  if (won) {
    flashCard(won);
  }
  if (tickets.value > 0) {
    await startSession();
  }
}

function goDepositToClaim() {
  shell.closeScratch();
  void router.push('/deposit');
}

function onClose() {
  if (view.value !== 'main') {
    view.value = 'main';
    return;
  }
  shell.closeScratch();
}

function openRules() {
  view.value = 'rules';
}

async function loadDailyTasks() {
  loadingTasks.value = true;
  try {
    dailyDetail.value = await getActivityDetail(MX_DAILY_ID, sessionStore.token || undefined);
  } catch (error) {
    if (isAuthError(error)) {
      dailyDetail.value = null;
      return;
    }
    dailyDetail.value = null;
  } finally {
    loadingTasks.value = false;
  }
}

function openTasks() {
  view.value = 'tasks';
  void loadDailyTasks();
}

function taskButtonLabel(row: ScratchTaskRow) {
  if (row.action === 'goComplete') {
    return t('oro.scratch.goComplete');
  }
  if (row.action === 'goBet') {
    return t('oro.sign.goBet');
  }
  return t('oro.sign.claim');
}

async function onTaskAction(row: ScratchTaskRow) {
  if (row.action === 'claimed' || claimingTaskKey.value) {
    return;
  }
  if (row.action === 'goBet') {
    shell.closeScratch();
    shell.setLobbyTab('slots');
    void router.push('/');
    return;
  }
  if (row.action === 'goComplete') {
    shell.closeScratch();
    void router.push('/agent-share');
    return;
  }
  if (!sessionStore.token) {
    authModal.openAuth({ mode: 'login' });
    return;
  }
  claimingTaskKey.value = row.id;
  try {
    await claimActivityReward(MX_DAILY_ID, row.id, sessionStore.token);
    await Promise.all([loadDailyTasks(), loadAlbum()]);
    if (tickets.value > 0 && !sessionReady.value) {
      await startSession();
    }
  } catch (error) {
    if (isAuthError(error)) {
      return;
    }
    showToast(t('oro.scratch.unavailable'));
  } finally {
    claimingTaskKey.value = '';
  }
}

async function onClaimSet() {
  if (needFirstDeposit.value) {
    goDepositToClaim();
    return;
  }
  if (!canClaimSet.value || claiming.value || opening.value || confirming.value || !sessionStore.token) {
    return;
  }
  claiming.value = true;
  try {
    const result = await claimScratchSet(sessionStore.token, newScratchRequestNo('c'));
    cards.value = result.cards;
    canClaimSet.value = result.canClaimSet;
    resetSessionVisual();
    revealOpen.value = false;
    await loadAlbum();
  } catch (error) {
    if (isAuthError(error)) {
      return;
    }
    showToast(t('oro.scratch.unavailable'));
  } finally {
    claiming.value = false;
  }
}

watch(
  () => shell.scratchOpen,
  async (open) => {
    if (!open) {
      resetSessionVisual();
      revealOpen.value = false;
      highlightKey.value = '';
      view.value = 'main';
      dailyDetail.value = null;
      return;
    }
    view.value = 'main';
    void loadDailyTasks();
    const album = await loadAlbum();
    if (!album) {
      return;
    }
    if (album.pending) {
      await armScratch(album.pending.cardKey, album.pending.title, album.pending.clientRequestNo);
      return;
    }
    if (album.tickets > 0) {
      await startSession();
    }
  },
  { immediate: true },
);

onBeforeUnmount(() => {
  window.clearTimeout(highlightTimer);
});
</script>

<template>
  <div
    v-if="shell.scratchOpen"
    class="wufu-scratch-overlay"
    role="dialog"
    aria-modal="true"
    :aria-label="t('oro.scratch.title')"
  >
    <button class="wufu-scratch-overlay__hit" type="button" :aria-label="t('oro.common.close')" @click="onClose" />
    <div class="wufu-scratch-modal__panel">
      <div class="wufu-scratch-modal__toolbar">
        <button
          v-if="view === 'main'"
          class="wufu-scratch-modal__tool-btn"
          type="button"
          aria-label="help"
          @click="openRules"
        >
          ?
        </button>
        <button class="wufu-scratch-modal__tool-btn" type="button" :aria-label="t('oro.common.close')" @click="onClose">
          <svg class="icon-arrow icon_close" width="24" height="24" focusable="false" aria-hidden="true">
            <use :href="`${sprite}#icon-close`" />
          </svg>
        </button>
      </div>

      <div class="dialog_title">
        <img class="dialog_title_img" :src="oroMxScratchAssets.title" alt="" />
      </div>

      <div class="wufu-scratch-modal__body">
        <div v-show="view === 'main'" class="wufu-scratch-modal__view">
          <p class="wufu-scratch-modal__intro">
            {{ t('oro.scratch.introBefore') }}
            <span class="wufu-scratch-modal__intro-highlight">{{ COLLECT_TARGET }}</span>
            {{ t('oro.scratch.introAfter') }}
          </p>

          <div class="wufu-scratch-modal__scratch-zone">
            <img class="decoration_header" :src="oroMxScratchAssets.decorationHeader" alt="" />
            <div class="wufu-scratch-modal__scratch-wrap">
              <img class="wufu-scratch-modal__frame" :src="oroMxScratchAssets.frame" alt="" />
              <img class="decoration_bottom" :src="oroMxScratchAssets.decorationBottom" alt="" />
              <div class="wufu-scratch-modal__scratch-inner">
                <OroMxScratchCard
                  ref="scratchRef"
                  :prize-url="prizeKey ? cardSrc(prizeKey) : ''"
                  :cover-url="oroMxScratchAssets.cover"
                  :empty-text="t('oro.scratch.thanks')"
                  :disabled="scratchDisabled"
                  @complete="onScratchComplete"
                />
              </div>
            </div>
          </div>

          <div class="wufu-scratch-modal__actions">
            <button class="wufu-scratch-modal__btn wufu-scratch-modal__btn--secondary" type="button" disabled>
              {{ t('oro.scratch.chances', { n: tickets }) }}
            </button>
            <button class="wufu-scratch-modal__btn" type="button" @click="openTasks">
              {{ t('oro.scratch.getAttempts') }}
            </button>
          </div>

          <section class="wufu-scratch-modal__cards">
            <h3 class="wufu-scratch-modal__cards-title">{{ t('oro.scratch.cards') }}</h3>
            <div class="wufu-scratch-modal__cards-row">
              <div
                v-for="slot in collectSlots"
                :key="slot.cardKey"
                class="wufu-scratch-modal__card-slot"
                :class="{
                  'wufu-scratch-modal__card-slot--gray': slot.qty <= 0,
                  'wufu-scratch-modal__card-slot--won': highlightKey === slot.cardKey,
                }"
              >
                <img
                  class="wufu-scratch-modal__card-img"
                  :class="{ 'wufu-scratch-modal__card-img--gray': slot.qty <= 0 }"
                  :src="cardSrc(slot.cardKey)"
                  alt=""
                />
              </div>
            </div>
            <div class="wufu-scratch-modal__redeem-row">
              <button
                class="wufu-scratch-modal__btn wufu-scratch-modal__btn--secondary wufu-scratch-modal__btn--wide"
                type="button"
                :disabled="!canRedeem || claiming || opening || confirming"
                @click="onClaimSet"
              >
                {{ t('oro.scratch.redeem') }}
              </button>
              <button
                class="wufu-scratch-modal__btn wufu-scratch-modal__btn--wide"
                type="button"
                disabled
              >
                {{ t('oro.scratch.redeemWild') }}
              </button>
            </div>
          </section>
        </div>

        <div v-show="view === 'tasks'" class="wufu-task-panel wufu-scratch-modal__view">
          <h3 class="wufu-task-panel__title">{{ t('oro.scratch.taskTitle') }}</h3>
          <p class="wufu-task-panel__tickets">{{ t('oro.scratch.currentTickets', { n: tickets }) }}</p>
          <ul v-if="taskRows.length" class="wufu-task-panel__list">
            <li v-for="row in taskRows" :key="row.id" class="wufu-task-panel__item">
              <div class="wufu-task-panel__item-copy">
                <p class="wufu-task-panel__item-title">{{ row.name }}</p>
                <p class="wufu-task-panel__item-reward">{{ t('oro.scratch.cantidad', { n: row.chances }) }}</p>
              </div>
              <button
                class="wufu-task-panel__item-btn"
                type="button"
                :disabled="row.action === 'claimed' || claimingTaskKey === row.id"
                @click="onTaskAction(row)"
              >
                {{ taskButtonLabel(row) }}
              </button>
            </li>
          </ul>
          <p v-else-if="!loadingTasks" class="wufu-task-panel__state">{{ t('oro.scratch.noData') }}</p>
        </div>

        <div v-show="view === 'rules'" class="wufu-rules-panel wufu-scratch-modal__view">
          <div class="wufu-rules-panel__scroll">
            <div class="wufu-rules-panel__html">
              <div class="header">
                <h2>{{ t('oro.scratch.rulesH2') }}</h2>
                <h3>{{ t('oro.scratch.rulesH3') }}</h3>
              </div>
              <div class="section">
                <h3 class="section-title">{{ t('oro.scratch.rulesPlay') }}</h3>
                <div class="card-rules">
                  <h3>{{ t('oro.scratch.rulesCover') }}</h3>
                  <p>
                    <img :src="oroMxScratchAssets.rulesLetters" alt="" />
                  </p>
                  <p>{{ t('oro.scratch.rulesCollect') }}</p>
                  <p>{{ t('oro.scratch.rulesWithdraw') }}</p>
                  <p>{{ t('oro.scratch.rulesFair') }}</p>
                  <ul>
                    <li>
                      <strong>{{ t('oro.scratch.rulesJackpot') }}</strong>
                    </li>
                  </ul>
                </div>
              </div>
              <div class="section">
                <p>{{ t('oro.scratch.rulesRestrict') }}</p>
                <p>{{ t('oro.scratch.rulesUse') }}</p>
                <div class="game-tag-container">
                  <span class="game-tag">{{ t('oro.scratch.rulesTags') }}</span>
                </div>
              </div>
              <div class="section">
                <p>{{ t('oro.scratch.rulesOther') }}</p>
                <p>{{ t('oro.scratch.rulesAbuse') }}</p>
                <p>
                  <strong>{{ t('oro.scratch.rulesFinal') }}</strong>
                </p>
                <p>{{ t('oro.scratch.rulesLuck') }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div v-if="revealOpen" class="wufu-prize-reveal" role="dialog" aria-modal="true" aria-live="polite">
        <div class="wufu-prize-reveal__content">
          <p class="wufu-prize-reveal__title">{{ t('oro.scratch.congrats') }}</p>
          <img v-if="prizeKey && cardSrc(prizeKey)" class="wufu-prize-reveal__icon" :src="cardSrc(prizeKey)" alt="" />
          <p class="wufu-prize-reveal__name">{{ prizeTitle || t('oro.scratch.thanks') }}</p>
          <button class="wufu-prize-reveal__btn" type="button" @click="onRevealConfirm">
            {{ t('oro.scratch.confirm') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.wufu-scratch-overlay {
  position: absolute;
  inset: 0;
  z-index: 3600;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.42667rem;
  background: rgb(0 0 0 / 55%);
}

.wufu-scratch-overlay__hit {
  position: absolute;
  inset: 0;
  padding: 0;
  background: transparent;
  border: 0;
}

.wufu-scratch-modal__panel {
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  width: 100%;
  height: min(36rem, calc(100% - 0.85333rem));
  min-height: 0;
  padding: 0.53333rem 0.32rem;
  overflow: hidden;
  color: #fff;
  background: #232325;
  border-radius: 0.53333rem;
}

.wufu-scratch-modal__toolbar {
  display: flex;
  flex-shrink: 0;
  gap: 0.53333rem;
  align-items: center;
  justify-content: flex-end;
  margin-bottom: 0.53333rem;
}

.wufu-scratch-modal__tool-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 1.22667rem;
  height: 1.22667rem;
  padding: 0;
  color: #fff;
  font-size: 0.53333rem;
  font-weight: 700;
  line-height: 0.8rem;
  background: #2e2e36;
  border: 0;
  border-radius: 0.21333rem;
}

.icon_close {
  display: block;
  width: 0.44444rem;
  height: 0.44444rem;
  color: #fff;
}

.dialog_title {
  display: flex;
  flex-shrink: 0;
  justify-content: center;
  margin: 0 0 0.26667rem;
}

.dialog_title_img {
  display: block;
  width: 14.98667rem;
  height: 2.29333rem;
  object-fit: contain;
}

.wufu-scratch-modal__body {
  position: relative;
  flex: 1;
  min-height: 0;
}

.wufu-scratch-modal__view {
  position: absolute;
  inset: 0;
  overflow: hidden auto;
}

.wufu-scratch-modal__intro {
  margin: 0 0 0.26667rem;
  padding: 0 1.06667rem;
  color: #fff;
  font-size: 0.64rem;
  font-weight: 400;
  line-height: 1.2;
  text-align: center;
}

.wufu-scratch-modal__intro-highlight {
  color: #eaae1b;
}

.wufu-scratch-modal__scratch-zone {
  position: relative;
  width: 16.58667rem;
  max-width: 100%;
  margin: 0 auto 1.81333rem;
  padding-top: 2.61333rem;
}

.decoration_header {
  position: absolute;
  top: -0.32rem;
  left: 0;
  z-index: 10;
  width: 100%;
  pointer-events: none;
}

.wufu-scratch-modal__scratch-wrap {
  position: relative;
}

.wufu-scratch-modal__frame {
  display: block;
  width: 100%;
}

.decoration_bottom {
  position: absolute;
  bottom: -1.30667rem;
  left: -1.01333rem;
  z-index: 10;
  width: 4.29333rem;
  pointer-events: none;
}

.wufu-scratch-modal__scratch-inner {
  position: absolute;
  top: 0.82667rem;
  right: 0.74667rem;
  bottom: 0.82667rem;
  left: 0.74667rem;
  z-index: 2;
  overflow: hidden;
}

.wufu-scratch-modal__actions {
  display: flex;
  width: 13.65333rem;
  height: 2.34667rem;
  margin: 0 auto;
  overflow: hidden;
  background: #757577;
  border-radius: 0.42667rem;
}

.wufu-scratch-modal__btn {
  display: flex;
  flex: 1;
  align-items: center;
  justify-content: center;
  height: 100%;
  padding: 0 0.32rem;
  color: #fff;
  font-size: 0.8rem;
  font-weight: 700;
  line-height: 1.2;
  text-align: center;
  background: #ee1e40;
  border: 0;
  border-radius: 0.32rem;
}

.wufu-scratch-modal__btn--secondary {
  color: #d9d9d9;
  background: rgb(117 117 119 / 18%);
}

.wufu-scratch-modal__btn:disabled {
  color: #d9d9d9;
  background: rgb(117 117 119 / 18%);
  opacity: 1;
}

.wufu-scratch-modal__cards {
  margin: 0.96rem 0 0.42667rem;
  padding: 0.42667rem;
  border: 0.04444rem solid #ee1e40;
  border-radius: 0.37333rem;
  box-shadow: inset 0 0.10667rem 0.10667rem #c80f2e;
}

.wufu-scratch-modal__cards-title {
  margin: 0 0 0.42667rem;
  color: #fff;
  font-size: 0.8rem;
  font-weight: 700;
  line-height: 1.2;
}

.wufu-scratch-modal__cards-row {
  display: flex;
  gap: 0.21333rem;
  margin: 0 0 0.8rem;
}

.wufu-scratch-modal__card-slot {
  display: flex;
  flex: 1;
  align-items: center;
  justify-content: center;
  height: 4.34667rem;
  padding: 0.21333rem 0.32rem;
  overflow: hidden;
  background: rgb(196 196 196 / 40%);
  border: 0.08889rem solid #d9d9d9;
}

.wufu-scratch-modal__card-slot--won {
  border-color: #eaae1b;
}

.wufu-scratch-modal__card-img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.wufu-scratch-modal__card-img--gray {
  filter: grayscale(1);
}

.wufu-scratch-modal__redeem-row {
  display: flex;
  gap: 0.57778rem;
  align-items: center;
  justify-content: center;
}

.wufu-scratch-modal__btn--wide {
  height: 2.34667rem;
}

.wufu-task-panel {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
}

.wufu-task-panel__title {
  margin: 0 0 0.88rem;
  font-size: 0.96rem;
  font-weight: 700;
  text-align: center;
}

.wufu-task-panel__tickets {
  margin: 0 0 0.42667rem;
  font-size: 0.64rem;
  font-weight: 600;
}

.wufu-task-panel__list {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 0.64rem;
  min-height: 0;
  margin: 0;
  padding: 0;
  overflow-y: auto;
  list-style: none;
  touch-action: pan-y;
}

.wufu-task-panel__item {
  position: relative;
  display: flex;
  gap: 0.32rem;
  align-items: center;
  justify-content: space-between;
  height: 5.14667rem;
  padding: 1.06667rem;
  background: #2e2e36;
  border-radius: 0.42667rem;
}

.wufu-task-panel__item::before {
  position: absolute;
  top: -1.28rem;
  right: 6.66667rem;
  z-index: 0;
  width: 1.86667rem;
  height: 1.86667rem;
  margin: auto;
  background: #232325;
  border-radius: 50%;
  content: '';
}

.wufu-task-panel__item:last-child::after {
  position: absolute;
  right: 6.66667rem;
  bottom: -1.28rem;
  z-index: 0;
  width: 1.86667rem;
  height: 1.86667rem;
  margin: auto;
  background: #232325;
  border-radius: 50%;
  content: '';
}

.wufu-task-panel__item-copy {
  min-width: 0;
}

.wufu-task-panel__item-title {
  margin: 0 0 0.64rem;
  font-size: 0.96rem;
  font-weight: 200;
}

.wufu-task-panel__item-reward {
  margin: 0;
  font-size: 0.64rem;
  font-weight: 700;
}

.wufu-task-panel__item-btn {
  flex-shrink: 0;
  min-width: 4.98667rem;
  height: 2.34667rem;
  padding: 0 0.53333rem;
  color: #fff;
  font-size: 0.8rem;
  font-weight: 700;
  background: #ee1e40;
  border: 0;
  border-radius: 0.42667rem;
}

.wufu-task-panel__item-btn:disabled {
  background: #757577;
}

.wufu-task-panel__state {
  display: flex;
  flex: 1;
  align-items: center;
  justify-content: center;
  margin: 0;
  padding: 0.64rem 0;
  color: #757577;
  text-align: center;
}

.wufu-rules-panel__scroll {
  height: 100%;
  overflow: hidden auto;
}

.wufu-rules-panel__html {
  padding: 0 0.32rem 1.06667rem;
  color: #fff;
  font-size: 0.64rem;
  line-height: 1.5;
}

.wufu-rules-panel__html h2 {
  margin: 0 0 0.42667rem;
  font-size: 0.74667rem;
  font-weight: 700;
  line-height: 1.35;
  text-align: center;
}

.wufu-rules-panel__html h3 {
  margin: 0 0 0.42667rem;
  font-size: 0.64rem;
  font-weight: 700;
}

.wufu-rules-panel__html p,
.wufu-rules-panel__html ul {
  margin: 0 0 0.42667rem;
}

.wufu-rules-panel__html img {
  display: block;
  width: 100%;
  height: auto;
}

.game-tag-container {
  margin: 0.42667rem 0;
}

.game-tag {
  display: inline-block;
  padding: 0.21333rem 0.42667rem;
  color: #fff;
  background: #2e2e36;
  border-radius: 0.21333rem;
}

.wufu-prize-reveal {
  position: absolute;
  inset: 0;
  z-index: 4;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgb(0 0 0 / 55%);
}

.wufu-prize-reveal__content {
  display: flex;
  flex-direction: column;
  gap: 0.53333rem;
  align-items: center;
  width: 12.8rem;
  padding: 0.85333rem 0.64rem;
  text-align: center;
  background: #232325;
  border-radius: 0.42667rem;
}

.wufu-prize-reveal__title {
  margin: 0;
  font-size: 0.74667rem;
  font-weight: 700;
}

.wufu-prize-reveal__icon {
  width: 3.2rem;
  height: 3.2rem;
  object-fit: contain;
}

.wufu-prize-reveal__name {
  margin: 0;
  font-size: 0.64rem;
}

.wufu-prize-reveal__btn {
  width: 100%;
  height: 2.13333rem;
  color: #fff;
  font-size: 0.74667rem;
  font-weight: 700;
  background: #ee1e40;
  border: 0;
  border-radius: 0.32rem;
}
</style>
